// @ts-nocheck
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Tree } from "react-arborist";
import { useWorkspaceStore, useDocumentStore } from "@/stores";
import { api } from "@/lib/api-client";
import { ShareDialog } from "./ShareDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ChevronRight,
  FolderPlus,
  FilePlus,
  MoreHorizontal,
  Trash2,
  Edit2,
  Star,
  RefreshCw,
  FolderOpen,
  Folder as FolderIcon,
  FileText,
  Paperclip,
  ChevronDown,
  Share2,
  Upload,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Tree node type for react-arborist
interface TreeNode {
  id: string;
  name: string;
  type: "folder" | "page" | "attachment";
  icon?: string | null;
  isFavorite?: boolean;
  children?: TreeNode[];
}

export function DocumentTree() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspaceStore();
  const { documents, currentDocument, setCurrentDocument, addDocument, updateDocument, removeDocument, setDocuments, searchQuery } = useDocumentStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(400);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createType, setCreateType] = useState<"folder" | "page">("page");
  const [createParentId, setCreateParentId] = useState<string | null>(null);
  const [newItemName, setNewItemName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [shareDocId, setShareDocId] = useState<string | null>(null);
  const [shareDocTitle, setShareDocTitle] = useState("");
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importType, setImportType] = useState<"folder" | "files">("folder");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Update container height on resize
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Filter documents by search query - keep parent hierarchy
  const filteredDocuments = useMemo(() => {
    if (!searchQuery) return documents;
    
    const query = searchQuery.toLowerCase();
    const matchingDocs = new Set<string>();
    
    // First pass: find matching documents
    documents.forEach((doc) => {
      if (doc.title.toLowerCase().includes(query) || 
          (doc.plainContent && doc.plainContent.toLowerCase().includes(query))) {
        matchingDocs.add(doc.id);
      }
    });
    
    // Second pass: include all parent documents in the hierarchy
    const includeParents = (docId: string) => {
      const doc = documents.find(d => d.id === docId);
      if (doc?.parentId) {
        matchingDocs.add(doc.parentId);
        includeParents(doc.parentId);
      }
    };
    
    matchingDocs.forEach(includeParents);
    
    return documents.filter(doc => matchingDocs.has(doc.id));
  }, [documents, searchQuery]);

  // Convert flat documents to tree structure
  const treeData = useMemo(() => {
    const buildTree = (parentId: string | null): TreeNode[] => {
      return filteredDocuments
        .filter((doc) => doc.parentId === parentId && !doc.isArchived)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((doc) => ({
          id: doc.id,
          name: doc.title,
          type: doc.type as "folder" | "page" | "attachment",
          icon: doc.icon,
          isFavorite: doc.isFavorite,
          children: buildTree(doc.id),
        }));
    };
    return buildTree(null);
  }, [filteredDocuments]);

  // Load documents
  const loadDocuments = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setIsLoading(true);
    try {
      const data = await api.get<{ success: boolean; documents: any[] }>(`/documents?workspaceId=${currentWorkspace.id}`);
      if (data.success) {
        setDocuments(data.documents);
      }
    } catch (error) {
      console.error("Error loading documents:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace, setDocuments]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Create folder or document
  const createItem = async (type: "folder" | "page", parentId: string | null = null) => {
    if (!currentWorkspace) {
      toast.error("Selecciona un workspace primero");
      return;
    }

    setIsCreating(true);
    setCreateType(type);
    setCreateParentId(parentId);
    setNewItemName(type === "folder" ? "Nueva carpeta" : "Nuevo documento");
  };

  const submitCreate = async () => {
    if (!currentWorkspace || !newItemName.trim()) return;

    try {
      const data = await api.post<{ success: boolean; document: any }>('/documents', {
        title: newItemName,
        type: createType,
        workspaceId: currentWorkspace.id,
        parentId: createParentId,
        icon: createType === "folder" ? "📁" : "📄",
      });

      if (data.success) {
        addDocument(data.document);
        if (createType === "page") {
          setCurrentDocument(data.document);
        }
        toast.success(createType === "folder" ? "Carpeta creada" : "Documento creado");
      }
    } catch (error) {
      toast.error("Error al crear");
    } finally {
      setIsCreating(false);
      setNewItemName("");
      setCreateParentId(null);
    }
  };

  // Rename item
  const startRename = (id: string, name: string) => {
    setEditingId(id);
    setEditingName(name);
  };

  const submitRename = async () => {
    if (!editingId || !editingName.trim()) return;

    try {
      const data = await api.put<{ success: boolean; document: any }>('/documents', {
        id: editingId,
        title: editingName,
      });

      if (data.success) {
        updateDocument(editingId, { title: editingName });
        toast.success("Renombrado");
      }
    } catch {
      toast.error("Error al renombrar");
    } finally {
      setEditingId(null);
      setEditingName("");
    }
  };

  // Delete item
  const deleteItem = async (id: string) => {
    try {
      const data = await api.delete<{ success: boolean }>(`/documents?id=${id}`);
      if (data.success) {
        removeDocument(id);
        if (currentDocument?.id === id) {
          setCurrentDocument(null);
        }
        toast.success("Eliminado");
      }
    } catch {
      toast.error("Error al eliminar");
    }
  };

  // Toggle favorite
  const toggleFavorite = async (id: string, isFavorite: boolean) => {
    try {
      const data = await api.put<{ success: boolean; document: any }>('/documents', {
        id,
        isFavorite: !isFavorite,
      });
      if (data.success) {
        updateDocument(id, { isFavorite: !isFavorite });
      }
    } catch {
      toast.error("Error");
    }
  };

  // Move item (drag & drop)
  const handleMove = async (ids: string[], parentId: string | null) => {
    try {
      for (const id of ids) {
        await api.put('/documents', { id, parentId });
        updateDocument(id, { parentId });
      }
      toast.success("Movido");
    } catch {
      toast.error("Error al mover");
    }
  };

  // Import from folder or files
  const handleImportFiles = async (files: FileList) => {
    if (!currentWorkspace) {
      toast.error("Selecciona un workspace primero");
      return;
    }

    setIsImporting(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }
    formData.append("workspaceId", currentWorkspace.id);

    try {
      const data = await api.post<{ success: boolean; imported: number }>('/import/files', formData);
      if (data.success) {
        toast.success(`${data.imported} archivos importados`);
        loadDocuments();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error("Error al importar archivos");
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportFolder = async (files: FileList) => {
    if (!currentWorkspace) {
      toast.error("Selecciona un workspace primero");
      return;
    }

    setIsImporting(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }
    formData.append("workspaceId", currentWorkspace.id);

    try {
      const data = await api.post<{ success: boolean; imported: number }>('/import/folder', formData);
      if (data.success) {
        toast.success(`${data.imported} archivos importados`);
        loadDocuments();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error("Error al importar carpeta");
    } finally {
      setIsImporting(false);
    }
  };

  // Get icon for node - minimalista y consistente
  const getIcon = (node: any): React.ReactNode => {
    if (node.data.type === "folder") {
      // Icono minimalista: > para cerrado, ⌄ para abierto (o usar Chevron)
      if (node.children.length > 0) {
        return node.isOpen 
          ? <span className="w-3 h-3 flex items-center justify-center text-muted-foreground text-[10px] font-medium select-none">⌄</span>
          : <span className="w-3 h-3 flex items-center justify-center text-muted-foreground text-[10px] font-medium select-none">›</span>;
      }
      // Carpeta vacía: icono minimalista
      return <span className="w-3 h-3 flex items-center justify-center text-muted-foreground text-[10px] select-none">›</span>;
    }
    if (node.data.type === "attachment") {
      return <Paperclip className="w-3 h-3 text-muted-foreground" />;
    }
    // Documentos: icono minimalista en lugar de emoji
    return <span className="w-3 h-3 flex items-center justify-center text-muted-foreground text-[10px] select-none">•</span>;
  };

  // Tree node renderer
  const Node = ({ node, style, dragHandle }: { node: any; style: React.CSSProperties; dragHandle?: React.RefObject<HTMLDivElement> }) => {
    const isEditing = editingId === node.id;
    const isSelected = currentDocument?.id === node.id;

    return (
      <div
        ref={dragHandle}
        style={style}
        className={cn(
          "group flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition-colors select-none",
          isSelected ? "bg-primary/10 text-primary" : "hover:bg-accent"
        )}
        onClick={() => {
          if (node.data.type !== "folder") {
            const fullDoc = documents.find((d) => d.id === node.id);
            if (fullDoc) {
              setCurrentDocument(fullDoc);
              navigate("/");
            }
          }
          if (node.data.type === "folder" && node.children.length > 0) {
            node.toggle();
          }
        }}
      >
        {/* Icon/Arrow for folders */}
        <span className="shrink-0">
          {getIcon(node)}
        </span>

        {/* Name or edit input */}
        {isEditing ? (
          <Input
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={submitRename}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") submitRename();
              if (e.key === "Escape") {
                setEditingId(null);
                setEditingName("");
              }
            }}
            className="h-6 text-sm px-1 py-0 min-w-0 flex-1"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="flex-1 truncate text-sm min-w-0">{node.data.name}</span>
        )}

        {/* Favorite indicator */}
        {node.data.isFavorite && !isEditing && (
          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 shrink-0" />
        )}

        {/* Context menu */}
        {!isEditing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-accent rounded shrink-0"
              >
                <MoreHorizontal className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {node.data.type === "folder" && (
                <>
                  <DropdownMenuItem onClick={() => createItem("page", node.id)}>
                    <FilePlus className="w-4 h-4 mr-2" />
                    Nuevo documento
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => createItem("folder", node.id)}>
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Nueva subcarpeta
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => startRename(node.id, node.data.name)}>
                <Edit2 className="w-4 h-4 mr-2" />
                Renombrar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => toggleFavorite(node.id, node.data.isFavorite)}>
                <Star className="w-4 h-4 mr-2" />
                {node.data.isFavorite ? "Quitar de favoritos" : "Añadir a favoritos"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setShareDocId(node.id);
                setShareDocTitle(node.data.name);
              }}>
                <Share2 className="w-4 h-4 mr-2" />
                Compartir
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => deleteItem(node.id)} className="text-destructive focus:text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-2 border-b shrink-0">
        <span className="text-xs font-medium text-muted-foreground">DOCUMENTOS</span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => createItem("folder")}
            title="Nueva carpeta"
          >
            <FolderPlus className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => createItem("page")}
            title="Nuevo documento"
          >
            <FilePlus className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setShowImportDialog(true)}
            title="Importar archivos"
          >
            <Upload className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={loadDocuments}
            title="Actualizar"
          >
            <RefreshCw className={cn("w-3.5 h-3.5", isLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Tree container */}
      <div ref={containerRef} className="flex-1 overflow-hidden min-h-0">
        {treeData.length > 0 ? (
          <Tree
            data={treeData}
            openByDefault={false}
            width="100%"
            height={containerHeight}
            indent={16}
            rowHeight={28}
            paddingTop={4}
            paddingBottom={4}
            onMove={({ dragIds, parentId }) => handleMove(dragIds, parentId || null)}
          >
            {Node}
          </Tree>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
            <FolderOpen className="w-10 h-10 mb-2 opacity-50" />
            <p className="text-sm text-center font-medium">Sin documentos</p>
            <p className="text-xs text-center mt-1">
              Crea una carpeta o documento para comenzar
            </p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => createItem("folder")}>
                <FolderPlus className="w-3.5 h-3.5 mr-1" />
                Carpeta
              </Button>
              <Button size="sm" onClick={() => createItem("page")}>
                <FilePlus className="w-3.5 h-3.5 mr-1" />
                Documento
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {createType === "folder" ? (
                <>
                  <FolderIcon className="w-5 h-5" />
                  Nueva carpeta
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Nuevo documento
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Ingresa un nombre para el nuevo {createType === "folder" ? "carpeta" : "documento"}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            placeholder={createType === "folder" ? "Nombre de carpeta" : "Nombre de documento"}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitCreate();
            }}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreating(false)}>
              Cancelar
            </Button>
            <Button onClick={submitCreate}>Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Importar Archivos
            </DialogTitle>
            <DialogDescription>
              {isImporting 
                ? "Importando archivos, por favor espera..." 
                : "Importa archivos Markdown, imágenes o carpetas completas"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {isImporting ? (
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Importando archivos...</p>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <Button
                    variant={importType === "files" ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => {
                      setImportType("files");
                      fileInputRef.current?.click();
                    }}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Archivos
                  </Button>
                  <Button
                    variant={importType === "folder" ? "default" : "outline"}
                    className="flex-1"
                    onClick={() => {
                      setImportType("folder");
                      const input = document.createElement("input");
                      input.type = "file";
                      (input as any).webkitdirectory = true;
                      input.onchange = (e) => {
                        const files = (e.target as HTMLInputElement).files;
                        if (files) handleImportFolder(files);
                      };
                      input.click();
                    }}
                  >
                    <FolderIcon className="w-4 h-4 mr-2" />
                    Carpeta
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".md,.markdown,.txt,.png,.jpg,.jpeg,.gif,.svg"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) handleImportFiles(e.target.files);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Formatos soportados: .md, .markdown, .txt, .png, .jpg, .gif, .svg
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)} disabled={isImporting}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      {shareDocId && (
        <ShareDialog
          open={!!shareDocId}
          onOpenChange={(open) => {
            if (!open) {
              setShareDocId(null);
              setShareDocTitle("");
            }
          }}
          documentId={shareDocId}
          documentTitle={shareDocTitle}
        />
      )}
    </div>
  );
}
